// Pure calculations behind the PMO Control Tower. No React, no Supabase, so every number on that screen
// is unit-tested. Nothing is estimated: a figure that is not recorded is reported as null ("not recorded").

export type HealthTone = 'on-track' | 'at-risk' | 'critical';

export interface PmoProject {
  id: string;
  external_id: string | null;
  name: string;
  health: string | null;
  progress: number | null;
  budget: number | null;
  spent: number | null;
  updated_at: string | null;
}

export function healthTone(health: string | null | undefined): HealthTone {
  const v = (health ?? '').toLowerCase();
  if (['critical', 'red', 'blocked', 'delayed', 'off-track'].includes(v)) return 'critical';
  if (['at-risk', 'amber', 'warning', 'watch'].includes(v)) return 'at-risk';
  return 'on-track';
}

const TONE_RANK: Record<HealthTone, number> = { critical: 0, 'at-risk': 1, 'on-track': 2 };

/** Worst health first, then least progress, so the PMO reads exceptions before good news. */
export function worstFirst<T extends { health: string | null; progress: number | null }>(projects: T[]): T[] {
  return [...projects].sort((a, b) =>
    TONE_RANK[healthTone(a.health)] - TONE_RANK[healthTone(b.health)] || (a.progress ?? 0) - (b.progress ?? 0));
}

export interface PortfolioSummary {
  total: number;
  atRisk: number;
  critical: number;
  avgProgress: number;
  /** Sum of recorded budgets; null when no project has a budget recorded. */
  budget: number | null;
  /** Spend on the projects that have a budget; null when budget is null. */
  spent: number | null;
  /** 0-100+, null when budget is null or zero. */
  budgetUsedPct: number | null;
  /** Projects with a recorded budget whose spend exceeds it. */
  overBudget: number;
  /** Projects not updated within `staleHours`. */
  stale: number;
}

export function summarisePortfolio(projects: PmoProject[], now: Date = new Date(), staleHours = 24): PortfolioSummary {
  const tones = projects.map((p) => healthTone(p.health));
  const budgeted = projects.filter((p) => typeof p.budget === 'number' && p.budget > 0);
  const budget = budgeted.length ? budgeted.reduce((s, p) => s + (p.budget as number), 0) : null;
  const spent = budget === null ? null : budgeted.reduce((s, p) => s + (p.spent ?? 0), 0);
  const cutoff = now.getTime() - staleHours * 3_600_000;
  return {
    total: projects.length,
    atRisk: tones.filter((t) => t === 'at-risk').length,
    critical: tones.filter((t) => t === 'critical').length,
    avgProgress: projects.length ? Math.round(projects.reduce((s, p) => s + (p.progress ?? 0), 0) / projects.length) : 0,
    budget,
    spent,
    budgetUsedPct: budget && spent !== null ? Math.round((spent / budget) * 100) : null,
    overBudget: budgeted.filter((p) => (p.spent ?? 0) > (p.budget as number)).length,
    stale: projects.filter((p) => !p.updated_at || new Date(p.updated_at).getTime() < cutoff).length,
  };
}

export interface WaitingItem {
  id: string;
  kind: 'approval' | 'decision';
  title: string;
  /** Approvals: when the output was created. Decisions: the due date (may be null). */
  date: string | null;
  severity: string | null;
  project: string | null;
}

/**
 * One queue of what is waiting on the PMO. Overdue decisions first, then by date ascending (oldest approval /
 * nearest due date first); undated items last. Stable for equal dates.
 */
export function waitingQueue(
  approvals: Array<{ id: string; title: string; severity: string | null; created_at: string }>,
  decisions: Array<{ id: string; title: string; project_name: string | null; due_date: string | null; priority: string | null }>,
  now: Date = new Date(),
): WaitingItem[] {
  const items: WaitingItem[] = [
    ...approvals.map((a): WaitingItem => ({ id: a.id, kind: 'approval', title: a.title, date: a.created_at, severity: a.severity, project: null })),
    ...decisions.map((d): WaitingItem => ({ id: d.id, kind: 'decision', title: d.title, date: d.due_date, severity: d.priority, project: d.project_name })),
  ];
  const overdue = (i: WaitingItem) => i.kind === 'decision' && !!i.date && new Date(i.date).getTime() < now.getTime();
  const time = (i: WaitingItem) => (i.date ? new Date(i.date).getTime() : Number.POSITIVE_INFINITY);
  return items
    .map((item, idx) => ({ item, idx }))
    .sort((a, b) => Number(overdue(b.item)) - Number(overdue(a.item)) || time(a.item) - time(b.item) || a.idx - b.idx)
    .map((x) => x.item);
}
